import { CogIcon, LockClosedIcon, TrashIcon, XIcon } from "@heroicons/react/solid";
import classNames from "classnames";
import React from "react";

import LabelBadge, { LabelBadgeVariant } from "../ui/LabelBadge";
import { BasicTooltip } from "../ui/Tooltip";

import { ReleaseLabelProps, ReleaseLabelVariant } from "./types";

const getBadgeVariant = (variant: ReleaseLabelVariant): LabelBadgeVariant => {
  if (variant === "error") {
    return "error";
  }
  if (variant === "dynamic") {
    return "environment";
  }
  if (variant === "pending") {
    return "environment";
  }
  if (variant === "creation") {
    return "environment";
  }
  if (variant === "destructive") {
    return "error";
  }
  if (variant === "updated") {
    return "environment";
  }
  return "environment";
};

const ReleaseLabel: React.FC<ReleaseLabelProps> = ({
  label,
  variant = "default",
  size = "default",
  onDelete,
  onClick,
  onPendingDeleteClick,
  isDragging = false,
  readOnly = false,
  showDeleteButton = true,
  activeBackground: _activeBackground = "bg-blue-100",
  className,
}) => {
  const isDynamic = variant === "dynamic";
  const isPending = variant === "pending";
  const isPendingDelete = variant === "pending-delete";
  const isCreation = variant === "creation";
  const isDestructive = variant === "destructive";
  const isUpdated = variant === "updated";

  const hasClickHandler = !!onClick;
  const hasDeleteHandler = !!onDelete;
  const isInteractive = (hasClickHandler || hasDeleteHandler) && !readOnly;

  const isProtected = label.is_protected;

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (readOnly) {
      return;
    }

    // If there's an onClick handler, use it (for opening config dialog)
    if (onClick) {
      onClick(label);
      return;
    }

    // Fall back to onDelete if no onClick (legacy behavior)
    if (onDelete) {
      onDelete(label);
    }
  };

  const handlePendingDeleteClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onPendingDeleteClick?.();
  };

  const badgeVariant: LabelBadgeVariant = getBadgeVariant(variant);

  return (
    <div
      key={`${label.name}-${label.id}`}
      className={classNames(
        "group/Label relative inline-flex w-auto max-w-full",
        isInteractive && "cursor-pointer",
        isDragging && "opacity-85 shadow-md",
        className,
      )}
      onClick={handleClick}
    >
      <LabelBadge
        label={label.name}
        variant={badgeVariant}
        size={size}
        className={classNames(
          "transition-all duration-150 ease-in-out",
          isDynamic && "!border-dashed !border-blue-500 !bg-white !text-blue-400 hover:!bg-blue-50",
          isPending &&
            "!border-dotted !border-amber-500 !bg-orange-50 !text-amber-600 hover:!border-amber-600 hover:!bg-orange-100 hover:shadow-sm",
          isPendingDelete && "!border-dotted !border-red-500 !bg-red-50 !text-red-600",
          isCreation && "!border-green-500 !bg-green-50 !text-green-700",
          isDestructive && "!border-red-500 !bg-red-50 !text-red-700",
          isUpdated && "!border-blue-500 !bg-blue-50 !text-blue-700",
          // Hover effect for interactive labels (config mode)
          isInteractive &&
            !isDynamic &&
            !isPending &&
            !isPendingDelete &&
            !isCreation &&
            !isDestructive &&
            !isUpdated &&
            badgeVariant !== "error" &&
            "hover:!border-blue-400 hover:!bg-blue-50 hover:!text-blue-600 hover:shadow-sm",
          // Hover effect for pending delete labels
          isPendingDelete &&
            isInteractive &&
            "hover:!border-red-600 hover:!bg-red-100 hover:shadow-sm",
        )}
      />
      {isProtected && !isPendingDelete && (
        <BasicTooltip content="Protected label - changes require approval">
          <div className="absolute -left-1.5 -top-1.5 z-10">
            <div className="relative">
              <LockClosedIcon className="h-3.5 w-3.5 rounded-full bg-gradient-to-br from-amber-500 to-amber-600 p-0.5 text-white shadow-md ring-2 ring-white" />
            </div>
          </div>
        </BasicTooltip>
      )}
      {isPendingDelete && (
        <div
          className="absolute -left-1.5 -top-1.5 z-10 cursor-pointer"
          onClick={handlePendingDeleteClick}
        >
          <div className="relative">
            <TrashIcon className="h-3.5 w-3.5 rounded-full bg-gradient-to-br from-red-500 to-red-600 p-0.5 text-white shadow-md ring-2 ring-white transition-transform hover:scale-110" />
          </div>
        </div>
      )}
      {!readOnly && onClick && !isPending && (
        <div
          className="absolute -right-1.5 -top-1.5"
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            onClick(label);
          }}
        >
          <CogIcon className="h-3.5 w-3.5 cursor-pointer rounded-full bg-blue-500 p-0.5 text-white opacity-0 shadow-sm transition-all duration-150 hover:bg-blue-600 group-hover/Label:opacity-100" />
        </div>
      )}
      {!readOnly &&
        !isDynamic &&
        !isCreation &&
        !isDestructive &&
        showDeleteButton &&
        onDelete &&
        !onClick && (
          <div
            className="absolute -right-1.5 -top-1.5"
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              onDelete(label);
            }}
          >
            <XIcon className="h-3.5 w-3.5 cursor-pointer rounded-full bg-red-500 p-0.5 text-white opacity-0 shadow-sm transition-all duration-150 hover:bg-red-600 group-hover/Label:opacity-100" />
          </div>
        )}
    </div>
  );
};

export default ReleaseLabel;
export { getEnvFlair } from "../ui/LabelBadge";
export type { Label, ReleaseLabelProps, ReleaseLabelSize, ReleaseLabelVariant } from "./types";
